import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";

export interface ISuggestion {
  placeId: string;
  mainText: string;
  text: string;
  matches: google.maps.places.StringRange[];
  place: google.maps.places.Place;
}

export function useGooglePlacesAutocomplete({ countryCode }: { countryCode: string }) {
  /** Places 라이브러리 로드 */
  const placesLib = useMapsLibrary("places");

  /** 자동완성 제안 목록 */
  const [suggestions, setSuggestions] = useState<ISuggestion[]>([]);
  /** suggestions을 보여줄지 관리하는 state */
  const [isSuggestionsVisible, setIsSuggestionsVisible] = useState(false);
  /** 세션 토큰 */
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  /** Places 라이브러리 로드 시 세션 토큰 생성 */
  useEffect(() => {
    if (placesLib) {
      sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
    }
  }, [placesLib]);

  /** 자동완성 제안 가져오기 */
  const fetchSuggestions = useCallback(
    async (input: string) => {
      if (!placesLib || !input.trim()) {
        setSuggestions([]);
        return;
      }

      try {
        const request: google.maps.places.AutocompleteRequest = {
          input,
          includedRegionCodes: [countryCode || "US"],
          includedPrimaryTypes: ["street_address", "establishment"],
          language: "en",
          sessionToken: sessionTokenRef.current ?? undefined,
        };

        const { suggestions: autocompleteSuggestions } =
          await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        const formattedSuggestions: ISuggestion[] = autocompleteSuggestions.flatMap((suggestion) => {
          const prediction = suggestion.placePrediction;
          if (!prediction) return [];
          return [
            {
              placeId: prediction.placeId,
              text: prediction.text.text || "",
              matches: prediction.text.matches,
              mainText: prediction.mainText?.text || "",
              place: prediction.toPlace(),
            },
          ];
        });

        setSuggestions(formattedSuggestions);
      } catch (error) {
        console.error("[useGooglePlacesAutocomplete] Error fetching suggestions:", error);
        setSuggestions([]);
      }
    },
    [placesLib, countryCode],
  );

  /** 장소의 세부 정보 가져오기 */
  const fetchPlaceDetails = useCallback(async (place: google.maps.places.Place) => {
    try {
      await place.fetchFields({
        fields: ["addressComponents"],
      });

      const addressComponents = place.addressComponents;
      if (!addressComponents) return null;

      return addressComponents;
    } catch (error) {
      console.error("[useGooglePlacesAutocomplete] Error fetching place details:", error);
      return null;
    }
  }, []);

  /** 디바운스 적용된 입력값 변경 핸들러 */
  const handleInputChange = useMemo(
    () =>
      // 입력 값이 변경된 후 자동 완성 기능이 실행되기까지의 지연 시간을 설정
      debounce((value: string) => {
        if (!value.trim()) return;
        void fetchSuggestions(value);
      }, 300),
    [fetchSuggestions],
  );

  /** 제안 목록 초기화 */
  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  /** 캐시 초기화 (세션 토큰 갱신) */
  const clearCache = useCallback(() => {
    if (placesLib) {
      sessionTokenRef.current = new placesLib.AutocompleteSessionToken();
    }
  }, [placesLib]);

  /** 국가 변경 시 자동완성 상태 초기화 */
  useEffect(() => {
    clearSuggestions();
    clearCache();
  }, [countryCode, clearSuggestions, clearCache]);

  /** 컴포넌트 언마운트 시 디바운스 타이머 정리 */
  useEffect(() => {
    return () => {
      handleInputChange.cancel();
    };
  }, [handleInputChange]);

  return {
    suggestions,
    fetchPlaceDetails,
    isSuggestionsVisible,
    setIsSuggestionsVisible,
    handleInputChange,
    clearSuggestions,
    clearCache,
  };
}
